import { useState, Fragment } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, Clock, MapPin, Users, Pencil, Globe, Ban, Award, Download, CheckCircle2, Trash2, ChevronDown, ChevronRight, StickyNote, Radio, UserPlus } from 'lucide-react'
import { adminApi } from '@/api/admin'
import { certificateDownloadUrl } from '@/api/public'
import { Breadcrumb } from '@/components/shared/Breadcrumb'
import { useTopBar } from '@/contexts/TopBarContext'
import type { CourseSessionDto, AttendanceStatus, AttendanceOutcome } from '@/api/admin'
import { Button } from '@/components/ui/button'
import SessionFormModal from './SessionFormModal'
import AddAttendeesModal from './AddAttendeesModal'
import ThirdPartyCertificateUploader from '@/features/admin/certificates/ThirdPartyCertificateUploader'
import AssignedThirdPartyCertificatesList from '@/features/admin/certificates/AssignedThirdPartyCertificatesList'
import { formatPriceLabel, isPriced as isSessionPriced } from '@/lib/pricing'
import { useAdminMoney } from '@/hooks/useMoney'
import { useAdminDates } from '@/hooks/useDates'
import { LiveRoomCard } from '@/features/live/LiveRoomCard'
import { useLiveStatus } from '@/features/live/useLiveStatus'
import { SignInPanel } from '@/features/attendance/SignInPanel'
import { sessionStatusUpdate } from './sessionUpdate'

const STATUS_BADGE: Record<string, string> = {
  Draft:     'bg-slate-100 text-slate-600',
  Published: 'bg-green-100 text-green-700',
  Cancelled: 'bg-red-100 text-red-600',
  Completed: 'bg-blue-100 text-blue-700',
}

export default function SessionDetailPage() {
  const { formatDate, formatTime, formatDateTime } = useAdminDates()
  const fmtDate = (iso: string) => {
    return formatDate(iso, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  }
  const fmtTime = (iso: string) => {
    return formatTime(iso)
  }
  const fmtDateTime = (iso: string) => {
    return formatDateTime(iso, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
  const { fmt: fmtMoney } = useAdminMoney()
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [attendanceEdits, setAttendanceEdits] = useState<Record<string, AttendanceStatus>>({})
  const [outcomeEdits, setOutcomeEdits] = useState<Record<string, AttendanceOutcome>>({})
  const [showCancelled, setShowCancelled] = useState(false)
  const [expandedNotesId, setExpandedNotesId] = useState<string | null>(null)
  const [notesDraft, setNotesDraft] = useState('')
  const [issueResult, setIssueResult] = useState<string | null>(null)
  const [showAddAttendees, setShowAddAttendees] = useState(false)
  const [emailMissingCerts, setEmailMissingCerts] = useState<boolean | null>(null)
  const [selectedTab, setTab] = useState<'details' | 'attendees' | 'live' | 'certificates'>('details')

  const { data: session, isLoading } = useQuery({
    queryKey: ['session', id],
    queryFn: () => adminApi.getSession(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: bookings = [], isLoading: bookingsLoading } = useQuery({
    queryKey: ['session-bookings', id],
    queryFn: () => adminApi.getSessionBookings(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: attendees = [] } = useQuery({
    queryKey: ['session-attendees', id],
    queryFn: () => adminApi.getSessionAttendees(id!).then(r => r.data),
    enabled: !!id,
  })

  const { data: pendingCertificates = [], isLoading: pendingCertificatesLoading } = useQuery({
    queryKey: ['session-pending-third-party-certificates', id],
    queryFn: () => adminApi.getPendingThirdPartySessionCertificates(id!).then(r => r.data),
    enabled: !!id && session?.status === 'Completed' && session?.courseIsThirdPartyCertified && !!session?.certificatesIssuedAt,
  })

  const publishMutation = useMutation({
    mutationFn: (s: CourseSessionDto) => adminApi.updateSession(s.id, sessionStatusUpdate(s, 'Published')),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', id] }),
  })

  const cancelBookingMutation = useMutation({
    mutationFn: (bookingId: string) => adminApi.cancelSessionBooking(id!, bookingId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session-bookings', id] })
      qc.invalidateQueries({ queryKey: ['session', id] })
    },
  })

  const deleteBookingMutation = useMutation({
    mutationFn: (bookingId: string) => adminApi.deleteSessionBooking(id!, bookingId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session-bookings', id] })
      qc.invalidateQueries({ queryKey: ['session', id] })
    },
  })

  const updateNotesMutation = useMutation({
    mutationFn: ({ bookingId, adminNotes }: { bookingId: string; adminNotes: string | null }) =>
      adminApi.updateSessionBookingNotes(id!, bookingId, adminNotes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session-bookings', id] }),
  })

  const completeMutation = useMutation({
    mutationFn: (s: CourseSessionDto) => adminApi.updateSession(s.id, sessionStatusUpdate(s, 'Completed')),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session', id] }),
  })

  const saveAttendanceMutation = useMutation({
    mutationFn: (items: { sessionAttendeeId: string; attendanceStatus: AttendanceStatus; outcome?: AttendanceOutcome | null }[]) =>
      adminApi.setSessionAttendance(id!, items),
    onSuccess: () => {
      setAttendanceEdits({})
      setOutcomeEdits({})
      qc.invalidateQueries({ queryKey: ['session-attendees', id] })
    },
  })

  const issueCertsMutation = useMutation({
    mutationFn: (opts?: { missingOnly?: boolean; sendEmails?: boolean }) => adminApi.issueSessionCertificates(id!, opts).then(r => r.data),
    onSuccess: (res) => {
      setIssueResult(
        `${res.certificatesGenerated} issued` +
        (res.alreadyIssued ? `, ${res.alreadyIssued} already had one` : '') +
        (res.skippedNotPresent ? `, ${res.skippedNotPresent} not marked present` : '') + '.')
      qc.invalidateQueries({ queryKey: ['session-attendees', id] })
      qc.invalidateQueries({ queryKey: ['session', id] })
    },
    onError: (e) => setIssueResult(
      (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Failed to issue certificates.'),
  })

  const { data: profile } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })

  // The Live tab only exists when Live is switched on for this tenant (and the session can still run a room).
  // Shares its cached query with LiveRoomCard, so this costs no extra request.
  const liveAvailable = !!session && session.status !== 'Draft' && session.status !== 'Cancelled'
  const { data: liveStatus } = useLiveStatus({ sessionId: id! }, !!id && liveAvailable)
  const showLiveTab = liveAvailable && !!liveStatus?.enabled
  const liveIsOpen = liveStatus?.delivery?.status === 'Open'
  // If the tab disappears while selected (e.g. the session gets cancelled), fall back to Details rather than a blank page.
  const tab = selectedTab === 'live' && !showLiveTab ? 'details' : selectedTab

  useTopBar(
    <Breadcrumb items={[
      { label: 'Public Sessions', to: '/admin/sessions' },
      { label: session?.courseName ?? '…' },
    ]} />
  )

  if (isLoading) return (
    <div className="flex justify-center py-20">
      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  )

  if (!session) return (
    <div className="text-center py-20 text-slate-500">Session not found.</div>
  )

  const publicUrl = profile?.slug
    ? `${window.location.origin}/sessions/${profile.slug}/${session.id}`
    : null

  const confirmed = bookings.filter(b => b.status === 'Confirmed')
  // People marked present who have no certificate yet (an attendance correction, or someone added afterwards).
  const missingCertCount = attendees.filter(a => a.attendanceStatus === 'Present' && a.outcome !== 'Failed' && !a.hasCertificate).length
  // A session from a while ago is usually a back catalogue entry: don't email people about it unless asked.
  const isLongAgo = session ? new Date(session.startsAt) < new Date(Date.now() - 14 * 24 * 3600 * 1000) : false
  const cancelled = bookings.filter(b => b.status === 'Cancelled')
  const isPriced = isSessionPriced(session.pricingModel, session.price, session.flatFeeAmount)
  const priceLabel = formatPriceLabel(session.pricingModel, session.price, session.flatFeeAmount, session.flatFeeCapacity, fmtMoney)

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <span className={`text-xs font-semibold rounded-full px-2.5 py-0.5 ${STATUS_BADGE[session.status]}`}>
          {session.status}
        </span>
      </div>

      {/* ── Tab switcher ── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit max-w-full overflow-x-auto [&>button]:shrink-0 [&>button]:whitespace-nowrap">
        <button
          onClick={() => setTab('details')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${tab === 'details' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Details
        </button>
        <button
          onClick={() => setTab('attendees')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${tab === 'attendees' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Users size={13} />
          Attendees
          <span className="text-xs font-semibold text-gray-400">{session.bookedPlaces}/{session.totalPlaces}</span>
        </button>
        {showLiveTab && (
          <button
            onClick={() => setTab('live')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${tab === 'live' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Radio size={13} />
            Live
            {liveIsOpen && <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" title="A live session is open" />}
          </button>
        )}
        <button
          onClick={() => setTab('certificates')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-1.5 ${tab === 'certificates' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <Award size={13} />
          Certificates
        </button>
      </div>

      {tab === 'details' && (<>
      {/* Session details card */}
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2.5">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <CalendarDays size={15} className="text-slate-400" />
              <span>
                {fmtDate(session.startsAt)}
                {(session.additionalDays?.length ?? 0) > 0 && (
                  <span className="ml-2 text-xs text-slate-400">
                    + {session.additionalDays.length} more day{session.additionalDays.length !== 1 ? 's' : ''}
                  </span>
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock size={15} className="text-slate-400" />
              {fmtTime(session.startsAt)}
              {session.endsAt && ` – ${fmtTime(session.endsAt)}`}
            </div>
            {(session.additionalDays?.length ?? 0) > 0 && session.additionalDays.map((d, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-slate-500 pl-5">
                <span className="text-xs text-slate-400 w-12">Day {i + 2}</span>
                {fmtDate(d.startsAt)} · {fmtTime(d.startsAt)}{d.endsAt && ` – ${fmtTime(d.endsAt)}`}
              </div>
            ))}
            {session.deliveryMethod === 'Online' ? (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Globe size={15} className="text-slate-400" />
                Online
                {session.meetingUrl && (
                  <a href={session.meetingUrl} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline truncate">
                    {session.meetingUrl}
                  </a>
                )}
              </div>
            ) : session.venue ? (
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <MapPin size={15} className="text-slate-400" />
                {session.venue.name}{session.venue.city ? `, ${session.venue.city}` : ''}
                {session.venue.postcode ? ` ${session.venue.postcode}` : ''}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <MapPin size={15} /> Venue to be confirmed
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Users size={15} className="text-slate-400" />
              {session.bookedPlaces} booked · {session.availablePlaces} remaining of {session.totalPlaces} places
            </div>
          </div>
          <div className="text-right shrink-0">
            {priceLabel ? (
              <p className="text-2xl font-bold text-slate-900">{priceLabel}</p>
            ) : (
              <p className="text-sm text-slate-400">No price set</p>
            )}
          </div>
        </div>

        {(session.trainerAssignments?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            {session.trainerAssignments.map(t => (
              <span key={t.trainerId} className="inline-flex items-center gap-1.5 text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-1">
                <span className="font-medium">{t.firstName} {t.lastName}</span>
                <span className="text-slate-400">{t.role}</span>
              </span>
            ))}
          </div>
        )}

        {session.notes && (
          <p className="text-sm text-slate-500 border-t border-slate-100 pt-3">{session.notes}</p>
        )}

        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil size={14} className="mr-1" /> Edit
          </Button>
          {session.status === 'Draft' && (
            <Button
              size="sm"
              onClick={() => publishMutation.mutate(session)}
              disabled={publishMutation.isPending}
            >
              <Globe size={14} className="mr-1" />
              {publishMutation.isPending ? 'Publishing…' : 'Publish'}
            </Button>
          )}
          {publicUrl && session.status === 'Published' && (
            <a href={publicUrl} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline">
                <Globe size={14} className="mr-1" /> View public page
              </Button>
            </a>
          )}
        </div>

        {publicUrl && session.status === 'Published' && (
          <div className="bg-slate-50 rounded-lg px-3 py-2 text-xs text-slate-500 font-mono break-all">
            {publicUrl}
          </div>
        )}
      </div>
      </>)}

      {/* ── Attendees tab ── */}
      {tab === 'attendees' && (<>
      <SignInPanel
        target={{ sessionId: session.id }}
        hidden={session.status !== 'Published'}
        onChanged={() => { qc.invalidateQueries({ queryKey: ['session-attendees', id] }); qc.invalidateQueries({ queryKey: ['session-bookings', id] }) }}
      />

      {/* Capacity bar */}
      <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="font-medium text-slate-700">Capacity</span>
          <span className="text-slate-500">{session.bookedPlaces} / {session.totalPlaces}</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-indigo-500 rounded-full transition-all"
            style={{ width: `${Math.min(100, (session.bookedPlaces / session.totalPlaces) * 100)}%` }}
          />
        </div>
      </div>

      {/* Bookings table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 text-sm">Attendees ({confirmed.length})</h2>
          {session.status !== 'Cancelled' && (
            <Button size="sm" variant="outline" onClick={() => setShowAddAttendees(true)}>
              <UserPlus size={14} className="mr-1" /> Add attendees
            </Button>
          )}
        </div>
        {bookingsLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : confirmed.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No bookings yet</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              <tr>
                <th className="text-left px-5 py-2.5">Name</th>
                <th className="text-left px-3 py-2.5">Email</th>
                <th className="text-left px-3 py-2.5">Phone</th>
                <th className="text-center px-3 py-2.5">Places</th>
                <th className="text-left px-3 py-2.5">Booked</th>
                {isPriced && <th className="text-center px-3 py-2.5">Payment</th>}
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {confirmed.map(b => (
                <Fragment key={b.id}>
                <tr className="hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-800">
                    {b.contactName}
                    {(b.clientName || b.addedByAdmin) && (
                      <span className="block text-xs font-normal text-slate-400">
                        {b.addedByAdmin
                          ? (b.clientName ? `Client staff: ${b.clientName}` : 'Added by admin')
                          : `Booked by client: ${b.clientName}`}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-slate-600">{b.contactEmail}</td>
                  <td className="px-3 py-3 text-slate-500">{b.contactPhone ?? '—'}</td>
                  <td className="px-3 py-3 text-center">{b.placesRequested}</td>
                  <td className="px-3 py-3 text-slate-400 text-xs">{fmtDateTime(b.createdAt)}</td>
                  {isPriced && (
                    <td className="px-3 py-3 text-center">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        b.paidAt ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {b.paidAt ? 'Paid' : 'Unpaid'}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        className={`text-xs flex items-center gap-1 ${b.adminNotes ? 'text-indigo-600 hover:text-indigo-800' : 'text-slate-400 hover:text-slate-600'}`}
                        onClick={() => {
                          if (expandedNotesId === b.id) { setExpandedNotesId(null); return }
                          setExpandedNotesId(b.id)
                          setNotesDraft(b.adminNotes ?? '')
                        }}
                      >
                        <StickyNote size={12} /> {b.adminNotes ? 'Notes' : 'Add note'}
                      </button>
                      <button
                        className="text-red-400 hover:text-red-600 text-xs flex items-center gap-1"
                        onClick={() => {
                          if (confirm(`Cancel booking for ${b.contactName}?`))
                            cancelBookingMutation.mutate(b.id)
                        }}
                      >
                        <Ban size={12} /> Cancel
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedNotesId === b.id && (
                  <tr className="bg-slate-50/50">
                    <td colSpan={isPriced ? 6 : 5} className="px-5 py-3">
                      <div className="space-y-2">
                        <textarea
                          value={notesDraft}
                          onChange={e => setNotesDraft(e.target.value)}
                          placeholder="Internal note about this booking — visible to admins and assigned trainers"
                          rows={2}
                          className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                          autoFocus
                        />
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="ghost" onClick={() => setExpandedNotesId(null)}>Cancel</Button>
                          <Button
                            size="sm"
                            onClick={() => {
                              updateNotesMutation.mutate({ bookingId: b.id, adminNotes: notesDraft.trim() || null })
                              setExpandedNotesId(null)
                            }}
                            disabled={updateNotesMutation.isPending}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}

        {cancelled.length > 0 && (
          <div className="border-t border-slate-100">
            <button
              className="w-full flex items-center gap-1.5 px-5 py-3 text-xs text-slate-400 hover:text-slate-600"
              onClick={() => setShowCancelled(v => !v)}
            >
              {showCancelled ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              {cancelled.length} cancelled booking{cancelled.length !== 1 ? 's' : ''}
            </button>
            {showCancelled && (
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {cancelled.map(b => (
                    <tr key={b.id} className="bg-slate-50/50">
                      <td className="px-5 py-3 font-medium text-slate-500 line-through">{b.contactName}</td>
                      <td className="px-3 py-3 text-slate-400">{b.contactEmail}</td>
                      <td className="px-3 py-3 text-center text-slate-400">{b.placesRequested}</td>
                      <td className="px-3 py-3 text-right">
                        <button
                          className="text-red-400 hover:text-red-600 text-xs flex items-center gap-1 ml-auto"
                          onClick={() => {
                            if (confirm(`Permanently delete the cancelled booking for ${b.contactName}? This cannot be undone.`))
                              deleteBookingMutation.mutate(b.id)
                          }}
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Attendance */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
            <CheckCircle2 size={15} className="text-indigo-500" /> Attendance
          </h2>
          <div className="flex items-center gap-2">
            {session.status === 'Published' && (
              <Button size="sm" variant="outline"
                onClick={() => { if (confirm('Mark this session as completed? You can then issue certificates.')) completeMutation.mutate(session) }}
                disabled={completeMutation.isPending}>
                <CheckCircle2 size={14} className="mr-1" />
                {completeMutation.isPending ? 'Completing…' : 'Mark completed'}
              </Button>
            )}
          </div>
        </div>

        {session.status === 'Draft' && (
          <p className="text-sm text-slate-400 text-center py-6 px-5">
            Publish the session and record attendance to issue certificates.
          </p>
        )}

        {session.status !== 'Draft' && attendees.length === 0 && (
          <p className="text-sm text-slate-400 text-center py-6">No attendees yet.</p>
        )}

        {session.status !== 'Draft' && attendees.length > 0 && (
          <>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="text-left px-5 py-2.5">Attendee</th>
                  <th className="text-left px-3 py-2.5">Company</th>
                  <th className="text-center px-3 py-2.5">Attendance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {attendees.map(a => {
                  const status = attendanceEdits[a.sessionAttendeeId] ?? a.attendanceStatus
                  const outcome = outcomeEdits[a.sessionAttendeeId] ?? a.outcome ?? 'Passed'
                  return (
                    <tr key={a.sessionAttendeeId} className="hover:bg-slate-50">
                      <td className="px-5 py-3">
                        <p className="font-medium text-slate-800">{a.attendeeName}</p>
                        <p className="text-xs text-slate-400">{a.attendeeEmail}</p>
                      </td>
                      <td className="px-3 py-3 text-slate-500">{a.companyName ?? '—'}</td>
                      <td className="px-3 py-3">
                        <div className="flex justify-center items-center gap-1">
                          {(['Present', 'Absent', 'NotRecorded'] as AttendanceStatus[]).map(s => (
                            <button
                              key={s}
                              onClick={() => setAttendanceEdits(prev => ({ ...prev, [a.sessionAttendeeId]: s }))}
                              className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                                status === s
                                  ? s === 'Present' ? 'bg-green-100 text-green-700 border-green-200'
                                    : s === 'Absent' ? 'bg-red-100 text-red-700 border-red-200'
                                    : 'bg-slate-200 text-slate-600 border-slate-300'
                                  : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50'
                              }`}
                            >
                              {s === 'NotRecorded' ? '—' : s}
                            </button>
                          ))}
                          {status === 'Present' && (
                            <button
                              title="Click to toggle pass/fail"
                              onClick={() => setOutcomeEdits(prev => ({
                                ...prev,
                                [a.sessionAttendeeId]: outcome === 'Failed' ? 'Passed' : 'Failed',
                              }))}
                              className={`text-xs px-2 py-1 rounded-md border transition-colors ml-1 ${
                                outcome === 'Failed'
                                  ? 'bg-amber-100 text-amber-700 border-amber-200'
                                  : 'bg-green-50 text-green-700 border-green-200'
                              }`}
                            >
                              {outcome === 'Failed' ? 'Failed' : 'Passed'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {(Object.keys(attendanceEdits).length > 0 || Object.keys(outcomeEdits).length > 0) && (
              <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setAttendanceEdits({}); setOutcomeEdits({}) }}>Discard</Button>
                <Button size="sm"
                  onClick={() => saveAttendanceMutation.mutate(
                    Array.from(new Set([...Object.keys(attendanceEdits), ...Object.keys(outcomeEdits)])).map(sessionAttendeeId => {
                      const attendee = attendees.find(a => a.sessionAttendeeId === sessionAttendeeId)
                      const attendanceStatus = attendanceEdits[sessionAttendeeId] ?? attendee?.attendanceStatus ?? 'NotRecorded'
                      const outcome = attendanceStatus === 'Present'
                        ? (outcomeEdits[sessionAttendeeId] ?? attendee?.outcome ?? 'Passed')
                        : null
                      return { sessionAttendeeId, attendanceStatus, outcome }
                    }))}
                  disabled={saveAttendanceMutation.isPending}>
                  {saveAttendanceMutation.isPending ? 'Saving…' : 'Save attendance'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      </>)}

      {/* ── Live tab ── only reachable when Live is enabled for the tenant (see showLiveTab). */}
      {tab === 'live' && <LiveRoomCard target={{ sessionId: session.id }} hidden={!liveAvailable} />}

      {/* ── Certificates tab ── */}
      {tab === 'certificates' && (
        session.status !== 'Completed' ? (
          <div className="text-center py-12 text-sm text-slate-400 bg-white border border-slate-200 rounded-xl">
            <Award size={28} className="mx-auto text-slate-200 mb-3" />
            Certificates become available once this session is completed.
          </div>
        ) : (<>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
              <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                <Award size={15} className="text-indigo-500" /> Certificates
              </h2>
              {!session.certificatesIssuedAt && (
                <Button size="sm"
                  onClick={() => { setIssueResult(null); issueCertsMutation.mutate(undefined) }}
                  disabled={issueCertsMutation.isPending}>
                  <Award size={14} className="mr-1" />
                  {issueCertsMutation.isPending ? (session.courseIsThirdPartyCertified ? 'Confirming…' : 'Issuing…') : (session.courseIsThirdPartyCertified ? 'Confirm completion' : 'Issue certificates')}
                </Button>
              )}
            </div>

            {session.certificatesIssuedAt && !session.courseIsThirdPartyCertified && missingCertCount > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 px-5 py-3 text-xs text-amber-800">
                <span>{missingCertCount} attendee{missingCertCount === 1 ? ' is' : 's are'} marked present without a certificate.</span>
                <span className="flex items-center gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" className="accent-indigo-600" checked={emailMissingCerts ?? !isLongAgo} onChange={e => setEmailMissingCerts(e.target.checked)} />
                    Email them
                  </label>
                  <Button size="sm" disabled={issueCertsMutation.isPending}
                    onClick={() => { setIssueResult(null); issueCertsMutation.mutate({ missingOnly: true, sendEmails: emailMissingCerts ?? !isLongAgo }) }}>
                    <Award size={14} className="mr-1" /> {issueCertsMutation.isPending ? 'Issuing…' : 'Issue missing certificates'}
                  </Button>
                </span>
              </div>
            )}

            {session.certificatesIssuedAt && (
              <p className="text-xs text-green-700 bg-green-50 border-b border-green-200 px-5 py-2 flex items-center gap-1.5">
                <CheckCircle2 size={13} />
                {session.courseIsThirdPartyCertified ? 'Completion confirmed' : 'Certificates issued'} on{' '}
                {formatDate(session.certificatesIssuedAt, { day: 'numeric', month: 'long', year: 'numeric' })}.
              </p>
            )}

            {session.courseIsThirdPartyCertified && !session.certificatesIssuedAt && (
              <p className="text-xs text-amber-700 bg-amber-50 border-b border-amber-200 px-5 py-2">
                {session.courseThirdPartyProviderName || 'The provider'} issues certificates for this course — CertSlate won't generate one.
                Confirming completion only sends attendees marked Present the optional "thanks for completing" email; upload each certificate below once it arrives.
              </p>
            )}

            {issueResult && (
              <div className="mx-5 mt-4 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-xs text-indigo-700">
                {issueResult}
              </div>
            )}

            {attendees.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No attendees yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-2.5">Attendee</th>
                    <th className="text-left px-3 py-2.5">Company</th>
                    <th className="text-right px-5 py-2.5">Certificate</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {attendees.map(a => (
                    <tr key={a.sessionAttendeeId} className="hover:bg-slate-50">
                      <td className="px-5 py-3">
                        <p className="font-medium text-slate-800">{a.attendeeName}</p>
                        <p className="text-xs text-slate-400">{a.attendeeEmail}</p>
                      </td>
                      <td className="px-3 py-3 text-slate-500">{a.companyName ?? '—'}</td>
                      <td className="px-5 py-3 text-right">
                        {a.hasCertificate && a.certificateId ? (
                          <a href={certificateDownloadUrl(a.certificateId)} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800">
                            <Download size={13} /> Download
                          </a>
                        ) : (
                          <span className="text-xs text-slate-300">Not issued</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

      {session.status === 'Completed' && session.courseIsThirdPartyCertified && session.certificatesIssuedAt && (
        <div>
          <h2 className="text-sm font-medium text-slate-800 mb-2">Upload certificates</h2>
          <ThirdPartyCertificateUploader
            pending={pendingCertificates.map(p => ({ id: p.sessionAttendeeId, name: p.fullName, completedDate: p.completedDate }))}
            isLoading={pendingCertificatesLoading}
            providerName={session.courseThirdPartyProviderName}
            emptyMessage="Nobody on this session is waiting on a certificate."
            onUploaded={() => {
              qc.invalidateQueries({ queryKey: ['session-pending-third-party-certificates', id] })
              qc.invalidateQueries({ queryKey: ['session-attendees', id] })
            }}
            upload={(sessionAttendeeId, file) => adminApi.uploadSessionAttendeeCertificate(sessionAttendeeId, file)}
          />
        </div>
      )}

      {session.status === 'Completed' && session.courseIsThirdPartyCertified && session.certificatesIssuedAt && (
        <div>
          <h2 className="text-sm font-medium text-slate-800 mb-2">Certificates on file</h2>
          <AssignedThirdPartyCertificatesList
            assigned={attendees.filter(a => a.hasCertificate && a.certificateId).map(a => ({
              id: a.sessionAttendeeId,
              name: a.attendeeName,
              completedDate: session.startsAt,
              certificateId: a.certificateId!,
              uploadedAt: a.certificateUploadedAt!,
            }))}
            emptyMessage="Nobody on this session has a certificate uploaded yet."
            onReuploaded={() => qc.invalidateQueries({ queryKey: ['session-attendees', id] })}
            reupload={(sessionAttendeeId, file) => adminApi.uploadSessionAttendeeCertificate(sessionAttendeeId, file)}
          />
        </div>
      )}
        </>)
      )}

      {showAddAttendees && (
        <AddAttendeesModal
          session={session}
          existingEmails={attendees.map(a => a.attendeeEmail)}
          onClose={() => setShowAddAttendees(false)}
          onAdded={() => {
            qc.invalidateQueries({ queryKey: ['session', id] })
            qc.invalidateQueries({ queryKey: ['session-bookings', id] })
            qc.invalidateQueries({ queryKey: ['session-attendees', id] })
            qc.invalidateQueries({ queryKey: ['session-pending-third-party-certificates', id] })
          }}
        />
      )}

      {editing && (
        <SessionFormModal
          session={session}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            qc.invalidateQueries({ queryKey: ['session', id] })
            qc.invalidateQueries({ queryKey: ['sessions'] })
          }}
        />
      )}
    </div>
  )
}
